import React, { useState } from 'react';
import { api } from '../services/api';

const ICONS = [
  { col:'#0d2b1a', e:'⛳' }, { col:'#534AB7', e:'🏌️' },
  { col:'#993C1D', e:'🏆' }, { col:'#185FA5', e:'🌊' }, { col:'#854F0B', e:'☀️' },
];

export function CreateGroupModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [icon, setIcon] = useState(ICONS[0]);
  const [invites, setInvites] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const emails = invites.split(',').map(s => s.trim()).filter(Boolean);
      await api.createGroup({ name, description: desc || undefined, color: icon.col, emoji: icon.e, inviteEmails: emails });
      onCreated();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3>Create a group</h3>
        <form onSubmit={submit}>
          <div className="form-group"><label className="form-label">Group name</label><input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Saturday Morning Crew" required /></div>
          <div className="form-group"><label className="form-label">Description (optional)</label><textarea className="form-input" value={desc} onChange={e => setDesc(e.target.value)} placeholder="What's this group about?" style={{ resize:'vertical', minHeight:65 }} /></div>
          <div className="form-group">
            <label className="form-label">Icon</label>
            <div style={{ display:'flex', gap:7, flexWrap:'wrap', marginTop:4 }}>
              {ICONS.map(i => (
                <div key={i.e} onClick={() => setIcon(i)} style={{ width:30, height:30, borderRadius:7, background:i.col, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:13, border: icon.e === i.e ? '2px solid #7ec99a' : '2px solid transparent' }}>{i.e}</div>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Invite members (comma-separated emails)</label>
            <input className="form-input" value={invites} onChange={e => setInvites(e.target.value)} placeholder="buddy@email.com, friend@email.com" />
            <div style={{ fontSize:11, color:'#7a7a6a', marginTop:3 }}>Invite links sent via email.</div>
          </div>
          {error && <p className="form-error">{error}</p>}
          <div className="modal-actions">
            <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-accent btn-sm" disabled={loading}>{loading ? 'Creating…' : 'Create group'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}


export function EditGroupModal({ group, onClose, onSaved, onDeleted }) {
  const [name, setName] = useState(group.name);
  const [desc, setDesc] = useState(group.description || '');
  const [icon, setIcon] = useState(ICONS.find(i => i.e === group.emoji) || ICONS[0]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);

  // Search-to-add state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [addedIds, setAddedIds] = useState([]);
  const [removingId, setRemovingId] = useState(null);

  // Invite link state (for non-Greentime users)
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [copied, setCopied] = useState(false);

  // v96: public join code state
  const [joinCode, setJoinCode] = useState(group.join_code || null);
  const [joinCodeEnabled, setJoinCodeEnabled] = useState(!!group.join_code_enabled);
  const [joinCodeLoading, setJoinCodeLoading] = useState(false);
  const [joinCodeError, setJoinCodeError] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [joinLinkCopied, setJoinLinkCopied] = useState(false);
  const joinUrl = joinCode ? `${window.location.origin}/join/${joinCode}` : '';

  React.useEffect(() => {
    api.getMembers(group.id).then(setMembers).catch(() => {});
  }, [group.id]);

  // Debounced search
  React.useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await api.searchFriends(searchQuery);
        // Filter out people already in the group or already added this session
        setSearchResults(results.filter(u =>
          !members.some(m => m.id === u.id) && !addedIds.includes(u.id)
        ));
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery, members, addedIds]);

  const handleAdd = async (user) => {
    setAddingId(user.id);
    try {
      await api.addMember(group.id, user.id);
      setAddedIds(prev => [...prev, user.id]);
      setMembers(prev => [...prev, {
        id: user.id,
        name: user.name,
        initials: user.initials,
        is_owner: false,
      }]);
      setSearchResults(prev => prev.filter(u => u.id !== user.id));
    } catch (err) { alert(err.message); }
    finally { setAddingId(null); }
  };

  const handleRemove = async (member) => {
    if (!window.confirm(`Remove ${member.name} from the group?`)) return;
    setRemovingId(member.id);
    try {
      await api.removeMember(group.id, member.id);
      setMembers(prev => prev.filter(m => m.id !== member.id));
    } catch (err) { alert(err.message); }
    finally { setRemovingId(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await api.updateGroup(group.id, { name, description: desc || undefined, color: icon.col, emoji: icon.e });
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${group.name}"? This cannot be undone.`)) return;
    try { await api.deleteGroup(group.id); onDeleted(); }
    catch (err) { alert(err.message); }
  };

  const handleGenerateInvite = async () => {
    setInviteSending(true); setInviteSent(false);
    try {
      const emails = inviteEmail.trim() ? [inviteEmail.trim()] : [];
      const { inviteUrl: url } = await api.createInvite(group.id, emails);
      setInviteUrl(url);
      if (emails.length > 0) setInviteSent(true);
    } catch (err) { alert(err.message); }
    finally { setInviteSending(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  // v96: join code handlers
  const handleEnableJoinCode = async () => {
    setJoinCodeLoading(true); setJoinCodeError('');
    try {
      const body = customCode.trim() ? { customCode: customCode.trim() } : {};
      const { joinCode: code, joinCodeEnabled: enabled } = await api.createOrRefreshJoinCode(group.id, body);
      setJoinCode(code); setJoinCodeEnabled(enabled); setCustomCode('');
    } catch (err) { setJoinCodeError(err.message); }
    finally { setJoinCodeLoading(false); }
  };

  const handleRegenerateJoinCode = async () => {
    if (!window.confirm('Generate a new code? The old code (and any QR codes/flyers using it) will stop working.')) return;
    setJoinCodeLoading(true); setJoinCodeError('');
    try {
      const { joinCode: code, joinCodeEnabled: enabled } = await api.createOrRefreshJoinCode(group.id, { regenerate: true });
      setJoinCode(code); setJoinCodeEnabled(enabled);
    } catch (err) { setJoinCodeError(err.message); }
    finally { setJoinCodeLoading(false); }
  };

  const handleDisableJoinCode = async () => {
    setJoinCodeLoading(true); setJoinCodeError('');
    try {
      await api.disableJoinCode(group.id);
      setJoinCodeEnabled(false);
    } catch (err) { setJoinCodeError(err.message); }
    finally { setJoinCodeLoading(false); }
  };

  const copyJoinLink = () => {
    navigator.clipboard.writeText(joinUrl).then(() => {
      setJoinLinkCopied(true); setTimeout(() => setJoinLinkCopied(false), 2000);
    });
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3>Edit group</h3>
        <form onSubmit={submit}>
          <div className="form-group"><label className="form-label">Group name</label><input className="form-input" value={name} onChange={e => setName(e.target.value)} required /></div>
          <div className="form-group"><label className="form-label">Description (optional)</label><textarea className="form-input" value={desc} onChange={e => setDesc(e.target.value)} style={{ resize:'vertical', minHeight:65 }} /></div>
          <div className="form-group">
            <label className="form-label">Icon</label>
            <div style={{ display:'flex', gap:7, flexWrap:'wrap', marginTop:4 }}>
              {ICONS.map(i => (
                <div key={i.e} onClick={() => setIcon(i)} style={{ width:30, height:30, borderRadius:7, background:i.col, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:13, border: icon.e === i.e ? '2px solid #7ec99a' : '2px solid transparent' }}>{i.e}</div>
              ))}
            </div>
          </div>

          {/* Members list */}
          {members.length > 0 && (
            <div className="form-group">
              <label className="form-label">Members ({members.length})</label>
              <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:150, overflowY:'auto' }}>
                {members.map(m => (
                  <div key={m.id} style={{ fontSize:12, color:'#4a4a3a', padding:'4px 8px', background:'#f4f1eb', borderRadius:6, display:'flex', alignItems:'center', gap:6 }}>
                    <span style={{ width:26, height:26, borderRadius:'50%', background:'#0d2b1a', color:'#7ec99a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:600, flexShrink:0 }}>
                      {m.initials}
                    </span>
                    <span style={{ flex:1 }}>{m.name}</span>
                    {m.is_owner
                      ? <span style={{ fontSize:10, color:'#7ec99a' }}>owner</span>
                      : (
                        <button
                          type="button"
                          onClick={() => handleRemove(m)}
                          disabled={removingId === m.id}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'#c45a3a', fontSize:14, lineHeight:1, padding:'0 2px', opacity: removingId === m.id ? 0.4 : 1 }}
                          title="Remove from group"
                        >
                          ×
                        </button>
                      )
                    }
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Greentime user directly */}
          <div className="form-group" style={{ borderTop:'1px solid #e8e4d8', paddingTop:12, marginTop:4 }}>
            <label className="form-label">Add a Greentime member</label>
            <div style={{ position:'relative' }}>
              <input
                className="form-input"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name or email…"
                autoComplete="off"
              />
              {searching && (
                <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:11, color:'#9a9a8a' }}>Searching…</span>
              )}
            </div>
            {searchResults.length > 0 && (
              <div style={{ border:'1px solid #e8e4d8', borderRadius:8, marginTop:4, overflow:'hidden' }}>
                {searchResults.map(u => (
                  <div key={u.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', borderBottom:'0.5px solid #f0ede4', background:'#fff' }}>
                    <span style={{ width:26, height:26, borderRadius:'50%', background:'#0d2b1a', color:'#7ec99a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:600, flexShrink:0 }}>
                      {u.initials}
                    </span>
                    <span style={{ flex:1, fontSize:13, color:'#2a2a1a' }}>{u.name}</span>
                    <button
                      type="button"
                      className="btn btn-accent btn-xs"
                      onClick={() => handleAdd(u)}
                      disabled={addingId === u.id}
                    >
                      {addingId === u.id ? '…' : '+ Add'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
              <div style={{ fontSize:12, color:'#9a9a8a', marginTop:4 }}>No Greentime users found — use the invite link below for non-members.</div>
            )}
          </div>

          {/* Invite link for non-Greentime users */}
          <div className="form-group" style={{ borderTop:'1px solid #e8e4d8', paddingTop:12 }}>
            <label className="form-label">Invite someone not on Greentime</label>
            <div style={{ display:'flex', gap:6 }}>
              <input
                className="form-input"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="their@email.com (optional)"
                style={{ flex:1 }}
                type="email"
              />
              <button
                type="button"
                className="btn btn-accent btn-sm"
                onClick={handleGenerateInvite}
                disabled={inviteSending}
                style={{ whiteSpace:'nowrap' }}
              >
                {inviteSending ? '…' : 'Get link'}
              </button>
            </div>
            <div style={{ fontSize:11, color:'#7a7a6a', marginTop:3 }}>
              Leave blank to just get a shareable link. Enter their email to also send it to them.
            </div>
            {inviteUrl && (
              <div style={{ background:'#e8f5ec', borderRadius:8, padding:'0.65rem 0.75rem', marginTop:'0.6rem' }}>
                <div style={{ fontSize:11, fontWeight:500, color:'#1a5c32', marginBottom:4 }}>Invite link (expires in 7 days)</div>
                <div style={{ fontFamily:'monospace', fontSize:11, color:'#0d2b1a', background:'#fff', border:'1px solid #c8e8d0', borderRadius:5, padding:'4px 8px', marginBottom:6, wordBreak:'break-all' }}>{inviteUrl}</div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <button type="button" className="btn btn-primary btn-xs" onClick={handleCopy}>{copied ? '✓ Copied!' : 'Copy link'}</button>
                  {inviteSent && <span style={{ fontSize:12, color:'#2e7d4f' }}>✓ Email sent</span>}
                </div>
              </div>
            )}
          </div>

          {/* v96: Public join code — durable, reusable, QR-friendly */}
          <div className="form-group" style={{ borderTop:'1px solid #e8e4d8', paddingTop:12 }}>
            <label className="form-label">Public join code</label>
            {joinCodeEnabled && joinCode ? (
              <>
                <div style={{ fontSize:11, color:'#7a7a6a', marginBottom:8 }}>
                  Anyone with this code — or the QR below — can join instantly. Good for a flyer, social post, or the clubhouse board.
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(joinUrl)}`}
                    alt="QR code to join"
                    style={{ width:100, height:100, borderRadius:8, border:'1px solid #e8e4d8', flexShrink:0 }}
                  />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:'monospace', fontSize:18, fontWeight:600, letterSpacing:'.08em', color:'#0d2b1a', background:'#f4f1eb', borderRadius:6, padding:'6px 10px', marginBottom:6, textAlign:'center' }}>
                      {joinCode}
                    </div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      <button type="button" className="btn btn-primary btn-xs" onClick={copyJoinLink}>{joinLinkCopied ? '✓ Copied!' : 'Copy link'}</button>
                      <button type="button" className="btn btn-outline btn-xs" onClick={handleRegenerateJoinCode} disabled={joinCodeLoading}>Regenerate</button>
                      <button type="button" className="btn btn-outline btn-xs" onClick={handleDisableJoinCode} disabled={joinCodeLoading}>Disable</button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize:11, color:'#7a7a6a', marginBottom:6 }}>
                  {joinCode
                    ? 'Currently off — turning it back on reuses the same code, so any flyers or QR codes already out there keep working.'
                    : "Not set up yet — generate a code anyone can use to join instantly, no invite required."}
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <input
                    className="form-input"
                    value={customCode}
                    onChange={e => setCustomCode(e.target.value)}
                    placeholder="Custom code (optional)"
                    style={{ flex:1 }}
                  />
                  <button type="button" className="btn btn-accent btn-sm" onClick={handleEnableJoinCode} disabled={joinCodeLoading} style={{ whiteSpace:'nowrap' }}>
                    {joinCodeLoading ? '…' : joinCode ? 'Re-enable' : 'Enable'}
                  </button>
                </div>
              </>
            )}
            {joinCodeError && <p className="form-error">{joinCodeError}</p>}
          </div>

          {error && <p className="form-error">{error}</p>}
          <div className="modal-actions" style={{ justifyContent:'space-between' }}>
            <button type="button" className="btn btn-danger btn-sm" onClick={handleDelete}>Delete group</button>
            <div style={{ display:'flex', gap:6 }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-accent btn-sm" disabled={loading}>{loading ? 'Saving…' : 'Save changes'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export function InviteModal({ group, onClose }) {
  const [emails, setEmails] = useState('');
  const [inviteUrl, setInviteUrl] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setSending(true);
    try {
      const emailList = emails.split(',').map(s => s.trim()).filter(Boolean);
      const { inviteUrl: url } = await api.createInvite(group.id, emailList);
      setInviteUrl(url);
      if (emailList.length > 0) setSent(true);
    } catch (err) { alert(err.message); }
    finally { setSending(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(inviteUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3>Invite to {group.name}</h3>
        <div className="form-group">
          <label className="form-label">Emails to invite (optional)</label>
          <input className="form-input" value={emails} onChange={e => setEmails(e.target.value)} placeholder="friend@email.com, buddy@email.com" />
        </div>
        <button className="btn btn-accent btn-sm" onClick={generate} disabled={sending}>
          {sending ? 'Generating…' : 'Generate invite link'}
        </button>

        {inviteUrl && (
          <div style={{ background:'#e8f5ec', borderRadius:8, padding:'0.75rem', marginTop:'0.75rem' }}>
            <div style={{ fontSize:11, fontWeight:500, color:'#1a5c32', textTransform:'uppercase', letterSpacing:'.05em' }}>Invite link</div>
            <div style={{ fontFamily:'monospace', fontSize:11, color:'#0d2b1a', background:'#fff', border:'1px solid #c8e8d0', borderRadius:5, padding:'4px 8px', margin:'0.5rem 0', wordBreak:'break-all' }}>{inviteUrl}</div>
            <button className="btn btn-primary btn-xs" onClick={copy}>{copied ? 'Copied!' : 'Copy link'}</button>
            {sent && <span style={{ fontSize:12, color:'#2e7d4f', marginLeft:8 }}>✓ Emails sent</span>}
          </div>
        )}

        <div style={{ fontSize:11, color:'#7a7a6a', marginTop:'0.75rem', lineHeight:1.5 }}>
          Link expires in 7 days. Anyone with the link can join the group.
        </div>
        <div className="modal-actions">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default CreateGroupModal;
